import { CellClickedEvent, GridApi, GridReadyEvent } from 'ag-grid-community';
import React, { useCallback } from 'react';
import styled from 'styled-components';
import { Task } from 'api/tasks';
import { CpBody03Primary } from 'components/atoms/customer-portal/typography';
import { Grid } from 'components/atoms/grid';
import { TableSkeleton } from 'components/atoms/table';
import { LoadingCell } from 'components/organisms/tasks/all-tasks-v2/components';
import { Task as ITask } from 'data/tasks';
import { useIsResponsive } from 'hooks/use-media-query';
import { useTaskDrawerQueryParamsFromRouter } from 'hooks/use-query-params';
import { TasksToBeShown } from './types';

const GridWrapper = styled.div<{
  fullWidth?: boolean;
}>`
  height: 100%;
  min-height: 100%;
  width: ${(props) => (props.fullWidth ? '100%' : 'calc(100% - 300px)')};
  background: var(--cp-white);
  padding: var(--cp-spacing-08);
  overflow-x: auto;
  overflow-y: hidden;

  @media (max-width: 768px) {
    width: 100%;
    border-radius: var(--border-radius-small);
    border: 1px solid var(--cp-border-subtle);
    padding: var(--cp-spacing-06);
    min-height: 600px;
  }

  @media (max-width: 480px) {
    padding: var(--cp-spacing-03);
  }
`;

const StyledGrid = styled(Grid)`
  * {
    font-family: var(--font-family);
  }
  .ag-root-wrapper.ag-ltr.ag-layout-normal {
    min-height: 500px;
  }

  padding-bottom: 3rem;
`;

const ViewNameWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: start;
  margin-bottom: var(--cp-spacing-08);

  @media (max-width: 768px) {
    display: none;
  }
`;

export interface CpListInTheProjectTasksListWidgetProps {
  rows: Record<string, unknown>[];
  gridApi: GridApi | null;
  onGridReady: (event: GridReadyEvent<unknown, unknown>) => void;
  viewName: string;
  tasksToBeShown: TasksToBeShown;
  isLoading: boolean;
  hideSidepane?: boolean;
}

export const CpListInTheProjectTasksListWidget: React.FC<CpListInTheProjectTasksListWidgetProps> =
  ({
    rows,
    gridApi,
    onGridReady,
    viewName,
    tasksToBeShown,
    isLoading,
    hideSidepane,
  }) => {
    const isResponsive = useIsResponsive();
    const { openTaskDrawer } = useTaskDrawerQueryParamsFromRouter();

    const onCellClicked = useCallback(
      (event: CellClickedEvent) => {
        if (
          event.colDef?.field === 'taskName' ||
          event.colDef?.field === 'status'
        ) {
          const task = event.data as ITask;

          openTaskDrawer(task?.project, task?.taskId);
        }
      },
      [openTaskDrawer],
    );

    return (
      <GridWrapper
        data-cy="cp.project-tasks-list.table"
        fullWidth={
          isResponsive ||
          tasksToBeShown === TasksToBeShown.CustomFilter ||
          hideSidepane
        }
      >
        {viewName ? (
          <ViewNameWrapper data-cy="cp.project-tasks-list.view-name">
            <CpBody03Primary>{viewName}</CpBody03Primary>
          </ViewNameWrapper>
        ) : (
          <></>
        )}
        {isLoading ? (
          <TableSkeleton columnCount={4} />
        ) : (
          <StyledGrid
            api={gridApi}
            onGridReady={onGridReady}
            rowData={rows ?? []}
            getRowId={(props) =>
              ((props?.data as Task)?.taskId as number | string).toString()
            }
            defaultColDef={{
              resizable: true,
              sortable: false,
              unSortIcon: false,
              editable: false,
              lockPinned: true,
              suppressMovable: true,
              suppressMenu: true,
              cellEditorPopup: true,
              cellEditorPopupPosition: 'over',
              suppressColumnsToolPanel: true,
              onCellClicked,
            }}
            suppressScrollOnNewData
            suppressCellFocus
            suppressContextMenu
            suppressFocusAfterRefresh
            suppressMaintainUnsortedOrder
            suppressDragLeaveHidesColumns
            loadingCellRenderer={LoadingCell}
          />
        )}
      </GridWrapper>
    );
  };
